import React, { FC, useMemo, useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { hubspotInsightsTargetAtom, hubspotPageAtom, hubspotUsersSearchAtom, organizationAtom, signedInUserAtom } from "../../state/atoms";
import { getHubSpotUsers, HubSpotUser, signUserOut } from "../../services/firebase";
import { HUBSPOT_REPORTS } from "../../pages/Insights/HubSpotInsights";
import { HsHelpIcon, HsMenu, HsSearchIcon, HsSettingsIcon } from "../HubSpotUi";
import { LogoSquircle } from "../Display/Logo";
import { hubSpotSettingsGroups } from "./HubSpotAppShell";
import { openSupportChat } from "../../services/supportChat";

/* Opens the support chat (Everything's; services/supportChat). */
const openSupport = () => openSupportChat();

interface Destination {
  id: string;
  label: string;
  section: string;
  /** Extra text it is found by (a user's email, a card's dashboard). */
  also?: string;
  go: () => void;
}

/* The org's HubSpot users, fetched once on the search's first use. */
let usersRequest: Promise<HubSpotUser[]> | null = null;
const loadUsers = () => {
  usersRequest ??= getHubSpotUsers()
    .then((r) => (r.status === 200 ? (r.result?.users ?? []).filter((u) => !u.archived) : []))
    .catch(() => {
      usersRequest = null;
      return [];
    });
  return usersRequest;
};

const MAX_PER_SECTION = 6;

/**
 * HubSpot's global search pill on the dark bar. Finds everything there is
 * to open: the Insights dashboards and every card on them, every settings
 * page, and (where the org has the Users page) its people by name or email.
 * Arrow keys move through the matches, Enter opens one, Escape clears.
 */
const TopBarSearch: FC<{ showUsers: boolean }> = ({ showUsers }) => {
  const setPage = useSetRecoilState(hubspotPageAtom);
  const setInsightsTarget = useSetRecoilState(hubspotInsightsTargetAtom);
  const setUsersSearch = useSetRecoilState(hubspotUsersSearchAtom);
  const organization = useRecoilValue(organizationAtom);
  const [users, setUsers] = useState<HubSpotUser[]>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const destinations = useMemo<Destination[]>(() => {
    const report = (dashboard: string, card?: string) => () => {
      setPage("insights");
      setInsightsTarget({ dashboard, card });
    };
    const reports = HUBSPOT_REPORTS.flatMap((d) => [
      { id: `dash-${d.dashboard}`, label: d.label, section: "Insights", go: report(d.dashboard) },
      // A dashboard's first card can share its name; the dashboard stands for both.
      ...d.cards.filter((c) => c !== d.label).map((c) => ({ id: `card-${d.dashboard}-${c}`, label: c, section: d.label, also: "report", go: report(d.dashboard, c) })),
    ]);
    const settings = hubSpotSettingsGroups(showUsers).flatMap((g) =>
      g.items.map((i) => ({ id: `page-${i.key}`, label: i.label, section: `Settings · ${g.heading}`, also: "settings", go: () => setPage(i.key) }))
    );
    // HubSpot's users, or until they load, the emails holding a licence.
    const known = users.length
      ? users.map((u) => ({ name: [u.firstName, u.lastName].filter(Boolean).join(" "), email: u.email }))
      : (organization?.allowedOwnerEmails ?? []).map((email) => ({ name: "", email }));
    const people = showUsers
      ? known
          .filter((u) => u.email)
          .map((u) => ({
            id: `user-${u.email}`,
            label: u.name || u.email,
            section: u.name ? u.email : "User",
            also: `${u.email} user`,
            go: () => {
              setPage("users");
              setUsersSearch(u.email);
            },
          }))
      : [];
    const jobChanges = { id: "page-job-changes", label: "Job changes", section: "boring", also: "job change review approve reject", go: () => setPage("job-changes") };
    return [...reports, jobChanges, ...settings, ...people];
  }, [showUsers, users, organization?.allowedOwnerEmails, setPage, setInsightsTarget, setUsersSearch]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return destinations.filter((d) => d.id.startsWith("dash-") || d.id.startsWith("page-"));
    const hits = destinations.filter((d) => `${d.label} ${d.also ?? ""}`.toLowerCase().includes(q));
    // Reports, then settings, then people; in each, names that start with the
    // query first, and only a few, so one kind can't crowd out the rest.
    const kinds = [["dash", "card"], ["page"], ["user"]];
    const starts = (d: Destination) => Number(!d.label.toLowerCase().startsWith(q));
    return kinds.flatMap((k) =>
      hits
        .filter((d) => k.includes(d.id.split("-")[0]))
        .sort((a, b) => starts(a) - starts(b))
        .slice(0, MAX_PER_SECTION)
    );
  }, [q, destinations]);
  const shown = open;

  const go = (d: Destination | undefined) => {
    if (!d) return;
    d.go();
    setQuery("");
    setOpen(false);
    input.current?.blur();
  };

  return (
    <div className="boring-hubspot-topbar__search" role="search">
      <input
        ref={input}
        type="search"
        value={query}
        placeholder="Find in boring"
        aria-label="Find in boring"
        aria-expanded={shown}
        aria-controls="boring-topbar-search-results"
        aria-autocomplete="list"
        role="combobox"
        onFocus={() => {
          setOpen(true);
          if (showUsers) loadUsers().then((u) => u.length && setUsers(u));
        }}
        onBlur={() => setOpen(false)}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            go(matches[active]);
          } else if (e.key === "Escape") {
            setQuery("");
            setOpen(false);
            input.current?.blur();
          }
        }}
      />
      <span className="boring-hubspot-topbar__search-icon" aria-hidden="true">
        <HsSearchIcon />
      </span>
      {shown && (
        <ul id="boring-topbar-search-results" className="boring-hubspot-topbar__results" role="listbox">
          {matches.map((d, i) => (
            <li
              key={d.id}
              role="option"
              aria-selected={i === active}
              className="boring-hubspot-topbar__result"
              ref={i === active ? (el) => el?.scrollIntoView({ block: "nearest" }) : undefined}
              onMouseEnter={() => setActive(i)}
              // mousedown, so the pick lands before the input's blur closes the list
              onMouseDown={(e) => {
                e.preventDefault();
                go(d);
              }}
            >
              <span>{d.label}</span>
              <small>{d.section}</small>
            </li>
          ))}
          {matches.length === 0 && <li className="boring-hubspot-topbar__noresult">Nothing matches “{query.trim()}”.</li>}
        </ul>
      )}
    </div>
  );
};

/**
 * The dashboard's top bar for HubSpot orgs: HubSpot's 44px #333 global
 * toolbar (reports-dashboard.jpg), with our smiley where HubSpot has its
 * sprocket, a "Find in boring" search for reports, settings and users beside
 * it like HubSpot's, round icon buttons for help and settings, a divider, and the
 * account menu (org, connection, sign out) on the right. Styles are in
 * styles/hubspot.css. Outreach orgs never render it.
 */
const HubSpotTopBar: FC = () => {
  const organization = useRecoilValue(organizationAtom);
  const user = useRecoilValue(signedInUserAtom);
  const setPage = useSetRecoilState(hubspotPageAtom);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const connected = organization?.isHubSpotAuthorized === true || !!organization?.hubSpotAuthentication;
  const name = organization?.name || user?.organizationName || "";
  const initial = (name || user?.email || "b").trim().charAt(0).toUpperCase();

  return (
    <header className="boring-hubspot-topbar">
      <span className="boring-hubspot-topbar__logo" role="img" aria-label="boring">
        <LogoSquircle />
      </span>
      <TopBarSearch showUsers={!!user?.isSegmentedAccount} />
      <span className="boring-hubspot-topbar__spacer" />
      <button type="button" className="boring-hubspot-topbar__icon" aria-label="Help" title="Help" onClick={openSupport}>
        <HsHelpIcon />
      </button>
      <button type="button" className="boring-hubspot-topbar__icon" aria-label="Settings" title="Settings" onClick={() => setPage("account")}>
        <HsSettingsIcon />
      </button>
      <span className="boring-hubspot-topbar__divider" aria-hidden="true" />
      <button
        type="button"
        className="boring-hubspot-topbar__account"
        aria-haspopup="true"
        aria-expanded={!!anchor}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <span className="boring-hubspot-topbar__avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="boring-hubspot-topbar__org">{name}</span>
        <span className="hs-caret hs-caret--md" aria-hidden="true" />
      </button>
      <HsMenu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ style: { marginTop: 8, minWidth: 240 } }}
      >
        <li role="none" className="boring-hubspot-topbar__menu-head">
          <strong>{name}</strong>
          {user?.email && <span>{user.email}</span>}
          <span className="boring-hubspot-topbar__menu-status" data-connected={connected ? "true" : "false"}>
            {connected ? "Connected to HubSpot" : "Not connected to HubSpot"}
          </span>
        </li>
        <li role="none" className="hs-menu__divider" />
        <li role="none">
          <button
            type="button"
            role="menuitem"
            className="hs-menu__item"
            onClick={() => {
              setAnchor(null);
              setPage("account");
            }}
          >
            Account settings
          </button>
        </li>
        <li role="none">
          <button type="button" role="menuitem" className="hs-menu__item" onClick={() => signUserOut()}>
            Sign out
          </button>
        </li>
      </HsMenu>
    </header>
  );
};

export default HubSpotTopBar;
